import { isFunctionalError, successfulResponseToObject, getHeaderCorrelationId } from '@axa-fr-axion/restful';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useEffect, useLayoutEffect } from 'react';

const CONTENT_TYPE = "application/json";
const getHeaders = (method) => {
  return {
    "Accept": CONTENT_TYPE,
    ...getHeaderCorrelationId(process.env.DISABLE_DELAY ?? ""),
    ...method !== "GET" && { "Content-Type": CONTENT_TYPE }
  };
};
const request = ({ url, method, body }) => {
  return fetch(
    url,
    {
      method,
      headers: getHeaders(method),
      body: body ? JSON.stringify(body) : void 0
    }
  ).then(successfulResponseToObject);
};
const get = (url) => request({ url, method: "GET" });
const post = (url, body) => request({ url, body, method: "POST" });
const getErrorHandlers = (callbacks) => {
  return async (queryError) => {
    const restError = queryError;
    const error = restError.body && await restError.body();
    if (isFunctionalError(error)) {
      callbacks?.onFunctionalError?.(error);
      return;
    }
    if (restError.isDatadomeBlocked) {
      callbacks?.onDatadomeBlocked?.(error);
      return;
    }
    callbacks?.onTechnicalError?.(error);
  };
};

const DATA_DOME_RESPONSE_PASSED_EVENT = "dd_response_passed";
const isDatadomeBlocked = (error) => {
  return !!error?.isDatadomeBlocked;
};
const useDatadomeHandler = (enabled, onSuccess) => {
  useEffect(() => {
    if (!enabled) {
      return () => {
      };
    }
    window.addEventListener(DATA_DOME_RESPONSE_PASSED_EVENT, (event) => {
      event.preventDefault();
      onSuccess();
    });
    return () => window.removeEventListener(DATA_DOME_RESPONSE_PASSED_EVENT, () => {
    });
  }, [enabled]);
};

const useRequest = ({
  url,
  method,
  body,
  onTechnicalError,
  onFunctionalError,
  onDatadomeBlocked,
  onSuccess,
  onError,
  ...queryOptions
}) => {
  const response = useQuery({
    queryFn: () => request({ url, method, body }),
    ...queryOptions
  });
  const datadomeBlocked = isDatadomeBlocked(response.error);
  useDatadomeHandler(datadomeBlocked, response.refetch);
  useLayoutEffect(() => {
    if (!response.isLoading && response.isSuccess) {
      onSuccess?.(response.data);
    }
  }, [response.data, response.isSuccess, response.isLoading]);
  useLayoutEffect(() => {
    if (!response.isLoading && response.isError) {
      if (onError) {
        onError(response.error);
        return;
      }
      getErrorHandlers({
        onFunctionalError,
        onTechnicalError,
        onDatadomeBlocked
      })(response.error);
    }
  }, [response.error, response.isError, response.isLoading]);
  return {
    ...response,
    isLoading: response.isLoading || datadomeBlocked
  };
};
const useGet = (params) => {
  return useRequest({ ...params, method: "GET" });
};
const usePost = (params) => {
  return useRequest({ ...params, method: "POST" });
};

const useRequestMutation = ({
  url,
  method,
  onTechnicalError,
  onFunctionalError,
  onDatadomeBlocked,
  onError,
  ...mutationOptions
}) => {
  const response = useMutation({
    ...mutationOptions,
    mutationFn: (body) => request({ url, method, body }),
    onError: (error, variables, context) => {
      if (onError) {
        onError(error, variables, context);
        return;
      }
      getErrorHandlers({
        onFunctionalError,
        onTechnicalError,
        onDatadomeBlocked
      })(error);
    }
  });
  const datadomeBlocked = isDatadomeBlocked(response.error);
  useDatadomeHandler(datadomeBlocked, () => {
    response.mutate(response.variables);
  });
  return {
    ...response,
    isPending: response.isPending || datadomeBlocked
  };
};
const useGetMutation = (options) => {
  return useRequestMutation({ ...options, method: "GET" });
};
const usePostMutation = (options) => {
  return useRequestMutation({ ...options, method: "POST" });
};

export { DATA_DOME_RESPONSE_PASSED_EVENT, get, getErrorHandlers, isDatadomeBlocked, post, useDatadomeHandler, useGet, useGetMutation, usePost, usePostMutation, useRequest, useRequestMutation };
